import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1400px] mx-auto">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
          >
            <span className="text-xl font-heading font-bold tracking-wide">
              虚空韩漫
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500'
                    : 'text-foreground-700 hover:text-primary-500'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  categoryOpen ? 'rotate-180' : ''
                }`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50 border border-background-200/70 rounded-lg p-3 grid grid-cols-4 gap-2 w-[560px] animate-fade-in-up">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      onClick={() => setCategoryOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer">
              <i className="ri-search-line text-foreground-600 text-lg"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer">
              <i className="ri-bookmark-line text-foreground-600 text-lg"></i>
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
            aria-label="打开菜单"
          >
            <i className={`text-foreground-600 text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4 animate-fade-in-up">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 whitespace-nowrap ${
                  location.pathname === '/'
                    ? 'bg-primary-100 text-primary-600'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                首页
              </Link>
              <div className="px-4 py-3 text-sm font-medium text-foreground-500">
                分类浏览
              </div>
              <div className="grid grid-cols-2 gap-1 px-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors duration-150 whitespace-nowrap ${
                      location.pathname === `/category/${cat.slug}`
                        ? 'bg-primary-100 text-primary-600'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
              <Link
                to="/about"
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 whitespace-nowrap ${
                  location.pathname === '/about'
                    ? 'bg-primary-100 text-primary-600'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}